import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    Button,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Divider,
    Breadcrumbs,
    Link,
    Tooltip,
    ButtonGroup,
    Collapse,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Cancel as CancelIcon,
    FileUpload as FileUploadIcon,
    FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import axios from '../../services/api';

interface Theme {
    theme_id: number;
    title: string;
}

interface QuestionPaper {
    paper_id: number;
    title: string;
    description: string;
    theme_id: number;
    theme: Theme;
    activation_date: string;
    expiry_date: string;
    grade_brackets: {
        A: { min: number; max: number };
        B: { min: number; max: number };
        C: { min: number; max: number };
        F: { min: number; max: number };
    };
    questions_count: number;
    certificate_path: string | null;
    created_at: string;
    updated_at: string;
}

interface Question {
    question_id: number;
    question_text: string;
    options: string[];
    correct_answer: number;
    order: number;
}

interface QuestionFormData {
    question_text: string;
    options: string[];
    correct_answer: number;
    order: number;
}

const initialFormData: QuestionFormData = {
    question_text: '',
    options: ['', '', '', ''],
    correct_answer: 0,
    order: 0,
};

const Questions: React.FC = () => {
    const { t } = useTranslation();
    const { paperId } = useParams<{ paperId: string }>();
    const [questionPaper, setQuestionPaper] = useState<QuestionPaper | null>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState<QuestionFormData>(initialFormData);
    const [error, setError] = useState<string | null>(null);
    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
    const { user } = useAuth();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLDivElement>(null);
    const [importing, setImporting] = useState(false);

    useEffect(() => {
        if (showForm && !editingQuestion && formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [showForm, editingQuestion]);

    const fetchQuestionPaper = useCallback(async () => {
        try {
            const response = await axios.get(`/quiz/papers/${paperId}`);
            setQuestionPaper(response.data);
        } catch (error: any) {
            console.error('Failed to fetch question paper:', error);
            setError(t('contentCreator.questions.errors.loadPaper'));
        }
    }, [paperId]);

    const fetchQuestions = useCallback(async () => {
        if (!user || user.role !== 'content_creator') {
            setError(t('contentCreator.dashboard.accessRequired'));
            setLoading(false);
            return;
        }

        try {
            await fetchQuestionPaper();
            const response = await axios.get(`/quiz/papers/${paperId}/questions`);
            setQuestions(response.data);
        } catch (error: any) {
            console.error('Failed to fetch questions:', error);
            setError(t('contentCreator.questions.errors.loadQuestions'));
        } finally {
            setLoading(false);
        }
    }, [user, paperId, fetchQuestionPaper]);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'correct_answer' ? parseInt(value, 10) : value
        }));
    }, []);

    const handleOptionChange = useCallback((index: number, value: string) => {
        setFormData(prev => ({
            ...prev,
            options: prev.options.map((opt, i) => i === index ? value : opt)
        }));
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        
        try {
            if (editingQuestion) {
                await axios.put(`/quiz/questions/${editingQuestion.question_id}`, formData);
            } else {
                await axios.post(`/quiz/papers/${paperId}/questions`, formData);
            }
            setShowForm(false);
            setFormData(initialFormData);
            setEditingQuestion(null);
            fetchQuestions();
        } catch (error: any) {
            console.error('Failed to save question:', error);
            setError(error.response?.data?.error || t('contentCreator.questions.errors.saveQuestion'));
        }
    }, [formData, editingQuestion, paperId, fetchQuestions]);

    const handleEdit = useCallback((question: Question) => {
        setEditingQuestion(question);
        setFormData({
            question_text: question.question_text,
            options: question.options,
            correct_answer: question.correct_answer,
            order: question.order,
        });
    }, []);

    const handleDelete = useCallback(async (questionId: number) => {
        if (!window.confirm(t('contentCreator.questions.deleteConfirm'))) return;
        
        try {
            await axios.delete(`/quiz/questions/${questionId}`);
            fetchQuestions();
        } catch (error: any) {
            console.error('Failed to delete question:', error);
            setError(error.response?.data?.error || t('contentCreator.questions.errors.deleteQuestion'));
        }
    }, [fetchQuestions]);

    const handleCancel = useCallback(() => {
        setShowForm(false);
        setFormData(initialFormData);
        setEditingQuestion(null);
    }, []);

    const handleDownloadTemplate = useCallback(async () => {
        try {
            const response = await axios.get(`/quiz/papers/${paperId}/questions/template`, {
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `question_paper_${paperId}_template.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('Failed to download template:', error);
            setError(t('contentCreator.questions.errors.downloadTemplate'));
        }
    }, [paperId]);

    const handleImportClick = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setImporting(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            await axios.post(`/quiz/papers/${paperId}/questions/import`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Reset file input
            if (event.target) {
                event.target.value = '';
            }

            // Refresh questions
            fetchQuestions();
        } catch (error: any) {
            console.error('Failed to import questions:', error);
            setError(error.response?.data?.error || t('contentCreator.questions.errors.importQuestions'));
        } finally {
            setImporting(false);
        }
    }, [paperId, fetchQuestions]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                                <Link
                                    component={RouterLink}
                                    to="/content-creator/papers"
                                    color="inherit"
                                >
                                    {t('contentCreator.questions.breadcrumbs.questionPapers')}
                                </Link>
                                <Typography color="text.primary">
                                    {questionPaper?.title || t('contentCreator.questions.paperDetails.loading')}
                                </Typography>
                            </Breadcrumbs>
                            <Typography variant="h4" gutterBottom>
                                {questionPaper?.title || t('contentCreator.questions.paperDetails.loading')}
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                {t('contentCreator.questions.paperDetails.theme')}: {questionPaper?.theme?.title || t('contentCreator.questions.paperDetails.loading')}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 1 }}>
                                {questionPaper?.description}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('contentCreator.questions.paperDetails.activePeriod')}: {new Date(questionPaper?.activation_date || '').toLocaleDateString('en-GB', { 
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    })} - {new Date(questionPaper?.expiry_date || '').toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    })}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h5">{t('contentCreator.questions.title')} ({questions.length})</Typography>
                            <Box display="flex" alignItems="center" gap={2}>
                                <ButtonGroup variant="contained">
                                    <Tooltip title={t('contentCreator.questions.importExport.exportTooltip')}>
                                        <Button
                                            color="primary"
                                            onClick={handleDownloadTemplate}
                                            startIcon={<FileDownloadIcon />}
                                        >
                                            {t('contentCreator.questions.importExport.export')}
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title={t('contentCreator.questions.importExport.importTooltip')}>
                                        <Button
                                            color="primary"
                                            onClick={handleImportClick}
                                            startIcon={<FileUploadIcon />}
                                            disabled={importing}
                                        >
                                            {importing ? t('contentCreator.questions.importExport.importing') : t('contentCreator.questions.importExport.import')}
                                        </Button>
                                    </Tooltip>
                                </ButtonGroup>
                                {!showForm && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            setEditingQuestion(null);
                                            setFormData({
                                                ...initialFormData,
                                                order: questions.length
                                            });
                                            setShowForm(true);
                                        }}
                                    >
                                        {t('contentCreator.questions.addQuestion')}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept=".xlsx"
                            onChange={handleFileChange}
                        />
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Typography color="error">{error}</Typography>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Paper>
                            <List>
                                {questions.map((question, index) => (
                                    <React.Fragment key={question.question_id}>
                                        <ListItem divider>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="subtitle1">
                                                        {t('contentCreator.questions.form.option', { number: index + 1 })}. {question.question_text}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Box sx={{ mt: 1 }}>
                                                        {question.options.map((option, i) => (
                                                            <Typography
                                                                key={i}
                                                                color={i === question.correct_answer ? "primary" : "textSecondary"}
                                                                sx={{ 
                                                                    ml: 2,
                                                                    fontWeight: i === question.correct_answer ? 'bold' : 'normal'
                                                                }}
                                                            >
                                                                {t('contentCreator.questions.form.option', { number: String.fromCharCode(65 + i) })}. {option}
                                                            </Typography>
                                                        ))}
                                                    </Box>
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    color="primary"
                                                    onClick={() => handleEdit(question)}
                                                    sx={{ mr: 1 }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    edge="end"
                                                    color="error"
                                                    onClick={() => handleDelete(question.question_id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        <Collapse in={editingQuestion?.question_id === question.question_id} timeout="auto">
                                            <Box sx={{ p: 3, bgcolor: 'background.paper' }}>
                                                <form onSubmit={handleSubmit}>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                fullWidth
                                                                label={t('contentCreator.questions.form.questionText')}
                                                                name="question_text"
                                                                value={formData.question_text}
                                                                onChange={handleInputChange}
                                                                required
                                                                multiline
                                                                rows={2}
                                                            />
                                                        </Grid>
                                                        {formData.options.map((option, index) => (
                                                            <Grid item xs={12} sm={6} key={index}>
                                                                <TextField
                                                                    fullWidth
                                                                    label={t('contentCreator.questions.form.option', { number: String.fromCharCode(65 + index) })}
                                                                    value={option}
                                                                    onChange={(e) => handleOptionChange(index, e.target.value)}
                                                                    required
                                                                />
                                                            </Grid>
                                                        ))}
                                                        <Grid item xs={12}>
                                                            <FormControl component="fieldset">
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    {t('contentCreator.questions.form.correctAnswer')}
                                                                </Typography>
                                                                <RadioGroup
                                                                    row
                                                                    name="correct_answer"
                                                                    value={formData.correct_answer}
                                                                    onChange={handleInputChange}
                                                                >
                                                                    {formData.options.map((_, index) => (
                                                                        <FormControlLabel
                                                                            key={index}
                                                                            value={index}
                                                                            control={<Radio />}
                                                                            label={String.fromCharCode(65 + index)}
                                                                        />
                                                                    ))}
                                                                </RadioGroup>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Box display="flex" gap={2}>
                                                                <Button
                                                                    type="submit"
                                                                    variant="contained"
                                                                    color="primary"
                                                                >
                                                                    {t('contentCreator.questions.form.save')}
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    onClick={handleCancel}
                                                                    startIcon={<CancelIcon sx={{ color: 'primary.main' }} />}
                                                                    sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                                                                >
                                                                    {t('contentCreator.questions.form.cancel')}
                                                                </Button>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </form>
                                            </Box>
                                        </Collapse>
                                    </React.Fragment>
                                ))}
                            </List>
                        </Paper>
                    </Grid>

                    {/* Add new question form */}
                    {showForm && !editingQuestion && (
                        <Grid item xs={12} ref={formRef}>
                            <Paper>
                                <Box sx={{ p: 3, bgcolor: 'background.paper' }}>
                                    <form onSubmit={handleSubmit}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label={t('contentCreator.questions.form.questionText')}
                                                    name="question_text"
                                                    value={formData.question_text}
                                                    onChange={handleInputChange}
                                                    required
                                                    multiline
                                                    rows={2}
                                                />
                                            </Grid>
                                            {formData.options.map((option, index) => (
                                                <Grid item xs={12} sm={6} key={index}>
                                                    <TextField
                                                        fullWidth
                                                        label={t('contentCreator.questions.form.option', { number: String.fromCharCode(65 + index) })}
                                                        value={option}
                                                        onChange={(e) => handleOptionChange(index, e.target.value)}
                                                        required
                                                    />
                                                </Grid>
                                            ))}
                                            <Grid item xs={12}>
                                                <FormControl component="fieldset">
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        {t('contentCreator.questions.form.correctAnswer')}
                                                    </Typography>
                                                    <RadioGroup
                                                        row
                                                        name="correct_answer"
                                                        value={formData.correct_answer}
                                                        onChange={handleInputChange}
                                                    >
                                                        {formData.options.map((_, index) => (
                                                            <FormControlLabel
                                                                key={index}
                                                                value={index}
                                                                control={<Radio />}
                                                                label={String.fromCharCode(65 + index)}
                                                            />
                                                        ))}
                                                    </RadioGroup>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box display="flex" gap={2}>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        color="primary"
                                                    >
                                                        {t('contentCreator.questions.addQuestion')}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={handleCancel}
                                                        startIcon={<CancelIcon sx={{ color: 'primary.main' }} />}
                                                        sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                                                    >
                                                        {t('contentCreator.questions.form.cancel')}
                                                    </Button>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </Box>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Container>
    );
};

export default Questions;
